import { JSX } from 'react';
import { Alert, Box, LinearProgress, Typography } from '@mui/material';
import { AccountAllowances, AllowancePool } from './hooks/customer-account-allowances';
import { poolInk, poolNote, poolReading } from './allowance-format';

/**
 * What the account's subscription entitles it to, on the tabs where an admin can spend it.
 *
 * The customer has had these numbers in the portal all along, on the organisations count
 * bar and the users screen, and is refused by them there. HQ had none of them, so an
 * admin adding a company or a seat holder was working blind against a limit the API now
 * enforces. 2026-09-10.
 */
const Tile: (props: { label: string, pool: AllowancePool|undefined, noun: string }) => JSX.Element =
    (props: { label: string, pool: AllowancePool|undefined, noun: string }): JSX.Element => {
        const note: string = poolNote(props.pool, props.noun);

        return (
            <Box sx={ {
                flex: '1 1 160px',
                minWidth: '150px',
                padding: '9px 12px',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                background: 'var(--wf-surface-2)',
            } }>
                <Typography sx={ {
                    fontSize: 'var(--wf-eyebrow)',
                    letterSpacing: 'var(--wf-track)',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    fontWeight: 700,
                } }>
                    { props.label }
                </Typography>
                <Typography sx={ {
                    fontSize: '17px',
                    fontWeight: 600,
                    lineHeight: 1.35,
                    fontFamily: 'var(--wf-font-mono)',
                    color: poolInk(props.pool) ?? 'text.primary',
                } }>
                    { poolReading(props.pool) }
                </Typography>
                { (note !== '') &&
                    <Typography sx={ { fontSize: '12px', lineHeight: 1.4, color: 'text.secondary', marginTop: '2px' } }>
                        { note }
                    </Typography>
                }
            </Box>
        );
    }

type CustomerAccountAllowanceBarProps = {
    allowances: AccountAllowances|undefined,
    loading: boolean,
    error: string,
    /** Which pools this tab can actually spend. */
    pools?: ('companies'|'domains'|'seats')[],
};

export const CustomerAccountAllowanceBar: (props: CustomerAccountAllowanceBarProps) => JSX.Element =
    (props: CustomerAccountAllowanceBarProps): JSX.Element => {
        const pools: ('companies'|'domains'|'seats')[] = props.pools ?? ['companies', 'domains', 'seats'];

        if (props.loading) {
            return <LinearProgress sx={ { margin: '10px 0', width: '100%' } } />;
        }

        if (props.error !== '') {
            // Say the numbers are missing rather than showing none: an empty space here
            // reads as "no limits", which is the opposite of the truth.
            return (
                <Alert severity="warning" sx={ { width: '100%', marginBottom: '14px' } }>
                    { props.error + ' The API still enforces them, so a write past the plan will be refused.' }
                </Alert>
            );
        }

        return (
            <Box sx={ { width: '100%' } }>
                { (props.allowances?.isSubscribed === false) &&
                    <Alert severity="info" sx={ { width: '100%', marginBottom: '10px' } }>
                        { 'This account has no active subscription, so it has only what every tier includes.' }
                    </Alert>
                }
                <Box sx={ { display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '14px' } }>
                    { pools.includes('companies') &&
                        <Tile label="Companies" pool={ props.allowances?.companies } noun="companies bolt-on" />
                    }
                    { pools.includes('domains') &&
                        <Tile label="Domains" pool={ props.allowances?.domains } noun="domains bolt-on" />
                    }
                    { pools.includes('seats') &&
                        <Tile label="Seats" pool={ props.allowances?.seats } noun="seat count" />
                    }
                </Box>
            </Box>
        );
    }
